import { SafeError, SafeType } from './safeAwait';
import { OutboundRequestPolicy } from './outboundRequestPolicy';

const OUTBOUND_REQUEST_CIRCUIT_BREAKER_KEY = '__BZ_OUTBOUND_REQUEST_CIRCUIT_BREAKERS__';

type CircuitBreakerState = {
  failures: number[];
  halfOpenInFlight: number;
  openedUntil?: number;
  state: 'closed' | 'half-open' | 'open';
};

let circuitBreakerStates: Record<string, CircuitBreakerState> = {};

const getGlobalWithCircuitBreakers = (): typeof globalThis & {
  [OUTBOUND_REQUEST_CIRCUIT_BREAKER_KEY]?: Record<string, CircuitBreakerState>;
} =>
  globalThis as typeof globalThis & {
    [OUTBOUND_REQUEST_CIRCUIT_BREAKER_KEY]?: Record<string, CircuitBreakerState>;
  };

const getCircuitBreakerStates = (): Record<string, CircuitBreakerState> => {
  const globalWithState = getGlobalWithCircuitBreakers();

  if (
    !globalWithState[OUTBOUND_REQUEST_CIRCUIT_BREAKER_KEY] ||
    typeof globalWithState[OUTBOUND_REQUEST_CIRCUIT_BREAKER_KEY] !== 'object'
  ) {
    globalWithState[OUTBOUND_REQUEST_CIRCUIT_BREAKER_KEY] = circuitBreakerStates;
  }

  circuitBreakerStates = globalWithState[OUTBOUND_REQUEST_CIRCUIT_BREAKER_KEY] ?? circuitBreakerStates;
  return circuitBreakerStates;
};

export const resetCircuitBreakerState = (): void => {
  circuitBreakerStates = {};
  getGlobalWithCircuitBreakers()[OUTBOUND_REQUEST_CIRCUIT_BREAKER_KEY] = circuitBreakerStates;
};

const getCircuitBreakerState = (policy: OutboundRequestPolicy): CircuitBreakerState => {
  const states = getCircuitBreakerStates();
  let circuitBreakerState = states[policy.name];
  if (!circuitBreakerState) {
    circuitBreakerState = {
      failures: [],
      halfOpenInFlight: 0,
      state: 'closed',
    };
    states[policy.name] = circuitBreakerState;
  }
  return circuitBreakerState;
};

const getOpenDurationWithJitter = (openMs: number): number => {
  const jitter = Math.floor(Math.random() * Math.max(Math.floor(openMs / 2), 1));
  return openMs + jitter;
};

function logCircuitStateChange(
  policyName: string,
  fromState: CircuitBreakerState['state'],
  toState: CircuitBreakerState['state'],
  reason: string,
): void {
  if (typeof window !== 'undefined') return;
  if (fromState === toState) return;

  console.warn(
    JSON.stringify({
      event: 'outbound_circuit_state_change',
      from: fromState,
      level: 'warn',
      outboundRequestPolicy: policyName,
      reason,
      timestamp: new Date().toISOString(),
      to: toState,
    }),
  );
}

export const circuitBreakerAllowsRequest = (policy: OutboundRequestPolicy): SafeType<undefined> => {
  if (!policy.circuitBreaker) return { ok: undefined };

  const circuitBreakerState = getCircuitBreakerState(policy);
  const now = Date.now();

  if (circuitBreakerState.state === 'open' && (circuitBreakerState.openedUntil ?? 0) > now) {
    return {
      err: new SafeError('outbound request circuit breaker is open', 'outbound_request_circuit_open', {
        outboundRequestPolicy: policy.name,
        status: 503,
      }),
    };
  }

  if (circuitBreakerState.state === 'open') {
    logCircuitStateChange(policy.name, 'open', 'half-open', 'open_duration_elapsed');
    circuitBreakerState.state = 'half-open';
    circuitBreakerState.halfOpenInFlight = 0;
  }

  if (circuitBreakerState.state === 'half-open') {
    if (circuitBreakerState.halfOpenInFlight >= policy.circuitBreaker.halfOpenMaxProbes) {
      return {
        err: new SafeError(
          'outbound request circuit breaker is half-open and probe limit is full',
          'outbound_request_circuit_open',
          {
            outboundRequestPolicy: policy.name,
            status: 503,
          },
        ),
      };
    }
    circuitBreakerState.halfOpenInFlight += 1;
  }

  return { ok: undefined };
};

export const releaseHalfOpenProbeReservation = (policy: OutboundRequestPolicy | undefined): void => {
  if (!policy?.circuitBreaker) return;
  const circuitBreakerState = getCircuitBreakerState(policy);
  if (circuitBreakerState.state === 'half-open' && circuitBreakerState.halfOpenInFlight > 0) {
    circuitBreakerState.halfOpenInFlight -= 1;
  }
};

export const recordCircuitBreakerResult = (policy: OutboundRequestPolicy | undefined, failed: boolean): void => {
  const circuitBreaker = policy?.circuitBreaker;
  if (!policy || !circuitBreaker) return;

  const circuitBreakerState = getCircuitBreakerState(policy);
  const now = Date.now();

  if (!failed) {
    if (circuitBreakerState.state === 'half-open') {
      logCircuitStateChange(policy.name, 'half-open', 'closed', 'probe_succeeded');
      circuitBreakerState.failures = [];
      circuitBreakerState.halfOpenInFlight = 0;
      circuitBreakerState.openedUntil = undefined;
      circuitBreakerState.state = 'closed';
      return;
    }
    circuitBreakerState.failures = circuitBreakerState.failures.filter(
      time => now - time <= circuitBreaker.failureWindowMs,
    );
    return;
  }

  if (circuitBreakerState.state === 'half-open') {
    circuitBreakerState.halfOpenInFlight = Math.max(circuitBreakerState.halfOpenInFlight - 1, 0);
    logCircuitStateChange(policy.name, 'half-open', 'open', 'probe_failed');
    circuitBreakerState.state = 'open';
    circuitBreakerState.openedUntil = now + getOpenDurationWithJitter(circuitBreaker.openMs);
    return;
  }

  circuitBreakerState.failures = [...circuitBreakerState.failures, now].filter(
    time => now - time <= circuitBreaker.failureWindowMs,
  );

  if (circuitBreakerState.failures.length >= circuitBreaker.failureThreshold && circuitBreakerState.state !== 'open') {
    logCircuitStateChange(policy.name, circuitBreakerState.state, 'open', 'failure_threshold_exceeded');
    circuitBreakerState.state = 'open';
    circuitBreakerState.openedUntil = now + getOpenDurationWithJitter(circuitBreaker.openMs);
    circuitBreakerState.halfOpenInFlight = 0;
  }
};
